import { useState } from "react";
import { Link } from "react-router-dom";
import * as CookieConsent from "vanilla-cookieconsent";
import logo from "@/assets/fractional-cpo-logo.png";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

interface PageLayoutProps {
  children: React.ReactNode;
}

const PageLayout = ({ children }: PageLayoutProps) => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const handleContactClick = () => {
    trackEvent('contact_click', { source: 'nav' });
    setIsContactOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#1a1a1a]">
      {/* Fixed Nav */}
      <nav className="fixed top-0 w-full z-50 bg-[#FAFAF8]/90 backdrop-blur-sm border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="FractionalCPO" className="h-10" />
          </Link>
          <div className="flex-1 flex justify-center">
            <div className="flex items-center gap-6">
              <Link to="/assessment" className="text-sm font-semibold text-[#002178]/70 hover:text-[#002178] transition-colors tracking-wide">5%</Link>
              <Link to="/pe" className="text-sm font-semibold text-[#002178]/70 hover:text-[#002178] transition-colors tracking-wide">PE</Link>
            </div>
          </div>
          <button onClick={handleContactClick} className="px-6 py-2.5 bg-[#002178] text-white text-sm font-semibold hover:scale-105 transition-transform">
            Let's chat
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-16">{children}</main>

      {/* Footer */}
      <footer className="bg-[#002178] py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          {/* Top: Logo + tagline + Contact CTA */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <Link to="/">
                <img src={logo} alt="FractionalCPO" className="h-10" />
              </Link>
              <span className="text-white/50 text-sm">Revenue growth through product leadership.</span>
            </div>
            <button
              onClick={handleContactClick}
              className="px-6 py-2.5 bg-white text-[#002178] text-sm font-semibold hover:scale-105 transition-transform w-fit"
            >
              Contact Us
            </button>
          </div>

          {/* 4 columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Work With Us</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/assessment" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/assessment' })}>The 5% Assessment</Link></li>
                <li><Link to="/ai" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/ai' })}>AI-Native Leadership</Link></li>
                <li><Link to="/pe" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/pe' })}>PE Firms</Link></li>
                <li><Link to="/case-study" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/case-study' })}>Case Study</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Learn</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/what-is-a-fractional-cpo" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/what-is-a-fractional-cpo' })}>What Is a FractionalCPO?</Link></li>
                <li><Link to="/fractional-cpo-cost" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-cost' })}>How much does a fractional CPO cost?</Link></li>
                <li><Link to="/product-led-growth" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/product-led-growth' })}>What is product growth?</Link></li>
                <li><Link to="/how-fractional-executives-work-together" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/how-fractional-executives-work-together' })}>How Fractional Execs Work Together</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Insights</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/fractional-cpo-vs-cmo" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-cmo' })}>FractionalCPO vs CMO</Link></li>
                <li><Link to="/fractional-cpo-vs-cto" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-cto' })}>FractionalCPO vs CTO</Link></li>
                <li><Link to="/fractional-cpo-vs-coo" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-coo' })}>FractionalCPO vs COO</Link></li>
                <li><Link to="/fractional-cpo-vs-consultant" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-consultant' })}>FractionalCPO vs Consultant</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/about" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/about' })}>About</Link></li>
                <li><button onClick={handleContactClick} className="hover:text-white transition-colors">Contact</button></li>
                <li><a href="https://www.linkedin.com/company/fractionalcpo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" onClick={() => trackEvent('outbound_click', { url: 'https://www.linkedin.com/company/fractionalcpo', label: 'linkedin' })}>LinkedIn</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between text-xs text-white/30 pt-6 border-t border-white/10">
            <p>&copy; {new Date().getFullYear()} FractionalCPO. All rights reserved.</p>
            <div className="flex items-center gap-4 mt-2 md:mt-0">
              <Link to="/privacy" className="hover:text-white/50 transition-colors" onClick={() => trackEvent('footer_nav', { to: '/privacy' })}>Privacy</Link>
              <Link to="/terms" className="hover:text-white/50 transition-colors" onClick={() => trackEvent('footer_nav', { to: '/terms' })}>Terms</Link>
              <button type="button" className="hover:text-white/50 transition-colors" onClick={() => CookieConsent.showPreferences()}>Cookie preferences</button>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
};

export default PageLayout;
